import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf/dist/jspdf.umd.min.js';

export const LANDSCAPE_PAGE_WIDTH_MM = 297;
export const LANDSCAPE_PAGE_HEIGHT_MM = 210;
export const PDF_PRINT_MARGIN_MM = 8;
export const LANDSCAPE_BILL_WIDTH = Math.round(
  ((LANDSCAPE_PAGE_WIDTH_MM - PDF_PRINT_MARGIN_MM * 2) * 96) / 25.4
);

function createSectionWrapper() {
  const wrapper = document.createElement('div');
  wrapper.style.width = `${LANDSCAPE_BILL_WIDTH}px`;
  wrapper.style.background = '#ffffff';
  wrapper.style.boxSizing = 'border-box';
  wrapper.style.padding = '4px';
  return wrapper;
}

function applyPdfTableStyles(root) {
  root.querySelectorAll('.week-bill-table, .week-bill-grand, .week-bill-header table').forEach((table) => {
    table.style.width = '100%';
    table.style.minWidth = '100%';
    table.style.borderCollapse = 'collapse';
  });

  root.querySelectorAll('.week-bill-table th, .week-bill-table td').forEach((cell) => {
    cell.style.border = 'none';
    cell.style.borderTop = '1px solid #b9bcbf';
    cell.style.borderBottom = '1px solid #b9bcbf';
  });

  root.querySelectorAll('.week-bill-row--cfbf td').forEach((cell) => {
    cell.style.background = '#dbf2dc';
  });

  root.querySelectorAll('.week-bill-row--auto-exit td').forEach((cell) => {
    cell.style.background = '#ffe8e8';
  });

  root.querySelectorAll('.week-bill-grand td').forEach((cell) => {
    cell.style.border = 'none';
    cell.style.borderTop = '1px solid #9ca3af';
    cell.style.borderBottom = '1px solid #9ca3af';
  });

  root.querySelectorAll('.week-bill-script-title').forEach((title) => {
    title.style.borderLeft = 'none';
    title.style.borderRight = 'none';
    title.style.borderTop = '1px solid #b9bcbf';
    title.style.borderBottom = 'none';
  });
}

function prepareCaptureRoot(element) {
  const captureHost = document.createElement('div');
  captureHost.style.cssText = `position:fixed;left:-10000px;top:0;width:${LANDSCAPE_BILL_WIDTH}px;background:#fff;`;

  const captureRoot = element.cloneNode(true);
  captureRoot.style.width = `${LANDSCAPE_BILL_WIDTH}px`;
  captureRoot.style.minWidth = `${LANDSCAPE_BILL_WIDTH}px`;
  captureRoot.style.maxWidth = `${LANDSCAPE_BILL_WIDTH}px`;
  captureRoot.style.padding = '4px';
  captureRoot.style.boxSizing = 'border-box';
  captureRoot.style.transform = 'none';
  captureRoot.style.transformOrigin = 'top left';
  applyPdfTableStyles(captureRoot);

  captureHost.appendChild(captureRoot);
  document.body.appendChild(captureHost);

  return { captureHost, captureRoot };
}

function measureWrapperHeight(wrapper, captureHost) {
  captureHost.appendChild(wrapper);
  const height = wrapper.scrollHeight;
  captureHost.removeChild(wrapper);
  return height;
}

function getMaxSectionHeightPx() {
  const printableWidth = LANDSCAPE_PAGE_WIDTH_MM - PDF_PRINT_MARGIN_MM * 2;
  const printableHeight = LANDSCAPE_PAGE_HEIGHT_MM - PDF_PRINT_MARGIN_MM * 2;
  return Math.floor((printableHeight / printableWidth) * LANDSCAPE_BILL_WIDTH);
}

function buildTableFragment(title, thead, bodyRows) {
  const section = document.createElement('section');
  section.className = 'week-bill-script-block';

  if (title) {
    section.appendChild(title.cloneNode(true));
  }

  const tableWrap = document.createElement('div');
  tableWrap.className = 'week-bill-table-wrap';

  const table = document.createElement('table');
  table.className = 'week-bill-table';

  if (thead) {
    table.appendChild(thead.cloneNode(true));
  }

  const tbody = document.createElement('tbody');
  bodyRows.forEach((row) => tbody.appendChild(row.cloneNode(true)));
  table.appendChild(tbody);
  tableWrap.appendChild(table);
  section.appendChild(tableWrap);

  return section;
}

function splitScriptBlockIntoSections(block, captureHost, maxSectionHeightPx) {
  const title = block.querySelector('.week-bill-script-title');
  const table = block.querySelector('.week-bill-table');
  if (!table) {
    const wrapper = createSectionWrapper();
    wrapper.appendChild(block.cloneNode(true));
    return [wrapper];
  }

  const thead = table.querySelector('thead');
  const bodyRows = [...table.querySelectorAll('tbody tr')];
  const totalRow = bodyRows.find((row) => row.classList.contains('week-bill-total-row'));
  const dataRows = bodyRows.filter((row) => !row.classList.contains('week-bill-total-row'));

  if (dataRows.length === 0) {
    const wrapper = createSectionWrapper();
    wrapper.appendChild(block.cloneNode(true));
    return [wrapper];
  }

  const sections = [];
  let currentRows = [];

  const pushChunk = (rows, includeTotal) => {
    const rowsForChunk = includeTotal && totalRow ? [...rows, totalRow] : [...rows];
    const fragment = buildTableFragment(title, thead, rowsForChunk);
    const wrapper = createSectionWrapper();
    wrapper.appendChild(fragment);
    applyPdfTableStyles(wrapper);
    sections.push(wrapper);
  };

  for (const row of dataRows) {
    const candidateRows = [...currentRows, row];
    const candidateFragment = buildTableFragment(title, thead, candidateRows);
    const candidateWrapper = createSectionWrapper();
    candidateWrapper.appendChild(candidateFragment);
    applyPdfTableStyles(candidateWrapper);

    const candidateHeight = measureWrapperHeight(candidateWrapper, captureHost);

    if (candidateHeight > maxSectionHeightPx && currentRows.length > 0) {
      pushChunk(currentRows, false);
      currentRows = [row];
    } else {
      currentRows = candidateRows;
    }
  }

  if (currentRows.length > 0) {
    pushChunk(currentRows, true);
  }

  if (sections.length > 0) {
    return sections;
  }

  const fallback = createSectionWrapper();
  fallback.appendChild(block.cloneNode(true));
  applyPdfTableStyles(fallback);
  return [fallback];
}

function buildPdfSections(captureRoot, captureHost) {
  const maxSectionHeightPx = getMaxSectionHeightPx();
  const header = captureRoot.querySelector('.week-bill-header');
  const blocks = [...captureRoot.querySelectorAll('.week-bill-script-block')];
  const grand = captureRoot.querySelector('.week-bill-grand');
  const sections = [];

  if (blocks.length === 0) {
    const wrapper = createSectionWrapper();
    if (header) wrapper.appendChild(header.cloneNode(true));
    applyPdfTableStyles(wrapper);
    sections.push(wrapper);
    return sections;
  }

  blocks.forEach((block, index) => {
    const blockSections = splitScriptBlockIntoSections(block, captureHost, maxSectionHeightPx);

    blockSections.forEach((sectionWrapper, sectionIndex) => {
      const pageWrapper = createSectionWrapper();
      const isFirstPage = index === 0 && sectionIndex === 0;
      const isLastPage = index === blocks.length - 1 && sectionIndex === blockSections.length - 1;

      if (isFirstPage && header) {
        pageWrapper.appendChild(header.cloneNode(true));
      }

      [...sectionWrapper.childNodes].forEach((child) => {
        pageWrapper.appendChild(child.cloneNode(true));
      });

      if (isLastPage && grand) {
        pageWrapper.appendChild(grand.cloneNode(true));
      }

      applyPdfTableStyles(pageWrapper);
      sections.push(pageWrapper);
    });
  });

  return sections;
}

async function renderSectionCanvas(sectionWrapper, captureHost) {
  captureHost.appendChild(sectionWrapper);
  const captureHeight = Math.max(sectionWrapper.scrollHeight, sectionWrapper.offsetHeight);

  const canvas = await html2canvas(sectionWrapper, {
    scale: 2,
    useCORS: true,
    logging: false,
    backgroundColor: '#ffffff',
    width: LANDSCAPE_BILL_WIDTH,
    height: captureHeight,
    windowWidth: LANDSCAPE_BILL_WIDTH,
    windowHeight: captureHeight,
    scrollX: 0,
    scrollY: 0,
  });

  captureHost.removeChild(sectionWrapper);
  return canvas;
}

function appendCanvasToPdf(pdf, canvas, layout, isFirstPage, pageImages) {
  const { margin, pageWidth, pageHeight, printableWidth, printableHeight } = layout;
  const imgWidth = printableWidth;
  const imgHeight = (canvas.height * imgWidth) / canvas.width;
  const pixelsPerMm = canvas.height / imgHeight;
  const maxSlicePx = Math.floor(printableHeight * pixelsPerMm);

  let sourceY = 0;
  let pageIndex = 0;

  while (sourceY < canvas.height) {
    const slicePx = Math.min(maxSlicePx, canvas.height - sourceY);
    const sliceCanvas = document.createElement('canvas');
    sliceCanvas.width = canvas.width;
    sliceCanvas.height = slicePx;

    const ctx = sliceCanvas.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, sliceCanvas.width, sliceCanvas.height);
    ctx.drawImage(
      canvas,
      0,
      sourceY,
      canvas.width,
      slicePx,
      0,
      0,
      canvas.width,
      slicePx
    );

    const drawHeight = slicePx / pixelsPerMm;
    const pageImage = sliceCanvas.toDataURL('image/jpeg', 0.98);

    if (!isFirstPage || pageIndex > 0) {
      pdf.addPage([pageWidth, pageHeight], 'landscape');
    }

    pdf.addImage(
      pageImage,
      'JPEG',
      margin,
      margin,
      imgWidth,
      drawHeight,
      undefined,
      'FAST'
    );

    if (pageImages) {
      pageImages.push(pageImage);
    }

    sourceY += slicePx;
    pageIndex += 1;
  }
}

async function buildLandscapePdfBlob(sections, captureHost) {
  const pdf = new jsPDF({
    orientation: 'landscape',
    unit: 'mm',
    format: 'a4',
    compress: true,
  });

  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const margin = PDF_PRINT_MARGIN_MM;
  const layout = {
    margin,
    pageWidth,
    pageHeight,
    printableWidth: pageWidth - margin * 2,
    printableHeight: pageHeight - margin * 2,
  };

  const pageImages = [];
  let isFirstPdfPage = true;

  for (const sectionWrapper of sections) {
    const sectionClone = sectionWrapper.cloneNode(true);
    const canvas = await renderSectionCanvas(sectionClone, captureHost);
    appendCanvasToPdf(pdf, canvas, layout, isFirstPdfPage, pageImages);
    isFirstPdfPage = false;
  }

  return {
    blob: pdf.output('blob'),
    pageImages,
  };
}

function isMobileDevice() {
  return /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent || '');
}

function isAndroidWebView() {
  const ua = navigator.userAgent || '';
  return /Android/i.test(ua) && (/wv\)/.test(ua) || /WebView/i.test(ua));
}

export function isInAppWebView() {
  return Boolean(
    window.ReactNativeWebView?.postMessage
    || window.webkit?.messageHandlers
    || isAndroidWebView()
  );
}

export function shouldUseInAppPdfHandling() {
  return isInAppWebView() || isMobileDevice();
}

function ensurePdfBlob(blob) {
  if (blob?.type === 'application/pdf') {
    return blob;
  }
  return new Blob([blob], { type: 'application/pdf' });
}

export function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      if (typeof reader.result !== 'string') {
        reject(new Error('Invalid PDF data'));
        return;
      }
      const base64 = reader.result.split(',')[1];
      if (!base64) {
        reject(new Error('Invalid PDF data'));
        return;
      }
      resolve(base64);
    };
    reader.onerror = () => reject(reader.error || new Error('Failed to read PDF'));
    reader.readAsDataURL(ensurePdfBlob(blob));
  });
}

export function createPdfObjectUrl(blob) {
  return URL.createObjectURL(ensurePdfBlob(blob));
}

export async function notifyNativePdfDownload(blob, fileName) {
  if (!isInAppWebView()) {
    return false;
  }

  const base64 = await blobToBase64(blob);
  window.ReactNativeWebView.postMessage(JSON.stringify({
    type: 'FILE_DOWNLOAD',
    mimeType: 'application/pdf',
    fileName,
    data: base64,
  }));
  return true;
}

async function triggerPdfFileDownload(pdfBlob, fileName) {
  const previewUrl = createPdfObjectUrl(pdfBlob);
  const link = document.createElement('a');
  link.href = previewUrl;
  link.download = fileName;
  link.type = 'application/pdf';
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => URL.revokeObjectURL(previewUrl), 1000);
}

export async function saveWeekBillPdfBlob(blob, fileName) {
  const pdfBlob = ensurePdfBlob(blob);

  if (shouldUseInAppPdfHandling()) {
    if (window.ReactNativeWebView?.postMessage) {
      await notifyNativePdfDownload(pdfBlob, fileName);
    }

    const blobUrl = createPdfObjectUrl(pdfBlob);
    return { mode: 'open', blobUrl };
  }

  await triggerPdfFileDownload(pdfBlob, fileName);
  return { mode: 'download' };
}

async function deliverPdfBlob(blob, fileName, pageImages = []) {
  if (shouldUseInAppPdfHandling()) {
    return {
      mode: 'preview',
      blob,
      pageImages,
    };
  }

  await triggerPdfFileDownload(ensurePdfBlob(blob), fileName);
  return { mode: 'download', blob };
}

export function buildWeekBillFileName(startDate, endDate) {
  return `week-bill-${startDate}-to-${endDate}.pdf`;
}

export async function downloadWeekBillPdf(element, fileName = 'week-bill.pdf') {
  if (!element) {
    return { success: false };
  }

  const { captureHost, captureRoot } = prepareCaptureRoot(element);

  try {
    const sections = buildPdfSections(captureRoot, captureHost);
    const { blob, pageImages } = await buildLandscapePdfBlob(sections, captureHost);
    const delivery = await deliverPdfBlob(blob, fileName, pageImages);
    return { success: true, fileName, ...delivery };
  } finally {
    document.body.removeChild(captureHost);
  }
}
